import React, { useState, useRef, useEffect } from 'react';
import { supabase } from "../integrations/supabase/client";
import { Building, MapPin, PoundSterling, Search, AlertCircle } from 'lucide-react';
import OpeningTimesSelector from './OpeningTimesSelector';

const PodAddForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    opening_times: '',
    price: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Address search states
  const [addressQuery, setAddressQuery] = useState('');
  const [predictions, setPredictions] = useState<any[]>([]);
  const [isLoadingPlaces, setIsLoadingPlaces] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [addressError, setAddressError] = useState('');
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Price options (per minute)
  const priceOptions = [
    { value: '0.10', label: '£0.10/min' },
    { value: '0.15', label: '£0.15/min' },
    { value: '0.20', label: '£0.20/min' },
    { value: '0.25', label: '£0.25/min' }
  ];

  // Google Places API integration
  useEffect(() => {
    const searchPlaces = (searchQuery: string) => {
      if (!searchQuery.trim()) {
        setPredictions([]);
        setShowPredictions(false);
        setAddressError('');
        return;
      }

      if (!window.google || !window.google.maps || !window.google.maps.places) {
        setAddressError('Google Maps is still loading. Please wait...');
        return;
      }

      setIsLoadingPlaces(true);
      setAddressError('');

      try {
        const autocompleteService = new window.google.maps.places.AutocompleteService();
        
        autocompleteService.getPlacePredictions(
          {
            input: searchQuery,
            componentRestrictions: { country: 'GB' },
            types: ['establishment', 'geocode']
          },
          (predictions, status) => {
            setIsLoadingPlaces(false);
            
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setPredictions(predictions);
              setShowPredictions(true);
              setAddressError('');
            } else if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              setPredictions([]);
              setShowPredictions(false);
              setAddressError('No places found. Try a different search term.');
            } else {
              setPredictions([]);
              setShowPredictions(false);
              setAddressError('Search failed. Please try again.');
            }
          }
        );
      } catch (err) {
        console.error('Places API error:', err);
        setIsLoadingPlaces(false);
        setAddressError('Search service unavailable. Please try again later.');
      }
    };

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      searchPlaces(addressQuery);
    }, 300);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [addressQuery]);

  const handlePlaceSelect = (prediction: any) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      setAddressError('Google Maps is still loading. Please wait...');
      return;
    }

    try {
      const placesService = new window.google.maps.places.PlacesService(document.createElement('div'));
      
      placesService.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['formatted_address']
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            setFormData(prev => ({
              ...prev,
              address: place.formatted_address || prediction.description
            }));
            setAddressQuery('');
            setPredictions([]);
            setShowPredictions(false);
            setAddressError('');
          } else {
            setAddressError('Unable to get place details. Please try another location.');
          }
        }
      );
    } catch (err) {
      console.error('Place details error:', err);
      setAddressError('Unable to get place details. Please try again.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOpeningTimesChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      opening_times: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      const { error } = await supabase
        .from('pods')
        .insert([{
          ...formData,
          price: parseFloat(formData.price) || 0
        }]);

      if (error) throw error;

      setSuccess(true);
      setFormData({
        name: '',
        address: '',
        opening_times: '',
        price: ''
      });
    } catch (err) {
      console.error('Error adding pod:', err);
      setError('Failed to add pod. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="name" className="flex items-center gap-2 text-sm font-medium text-[#374151] mb-2">
            <Building size={16} className="text-[#374151]" />
            Pod Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374151] focus:border-transparent"
            placeholder="Enter pod name"
          />
        </div>

        <div>
          <label htmlFor="address" className="flex items-center gap-2 text-sm font-medium text-[#374151] mb-2">
            <MapPin size={16} className="text-[#374151]" />
            Address
          </label>
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={addressQuery}
                onChange={(e) => setAddressQuery(e.target.value)}
                placeholder="Search for address..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374151] focus:border-transparent"
                onFocus={() => addressQuery && setShowPredictions(true)}
                onBlur={() => setTimeout(() => setShowPredictions(false), 200)}
              />
              {isLoadingPlaces && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#374151]"></div>
                </div>
              )}
            </div>

            {addressError && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <div className="flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle size={16} />
                  <span>{addressError}</span>
                </div>
              </div>
            )}

            {showPredictions && predictions.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                {predictions.map((prediction) => (
                  <button
                    key={prediction.place_id}
                    type="button"
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-gray-50"
                    onClick={() => handlePlaceSelect(prediction)}
                  >
                    <div className="flex items-start gap-3">
                      <MapPin size={16} className="text-[#374151] mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">
                          {prediction.structured_formatting?.main_text || prediction.description}
                        </div>
                        <div className="text-sm text-gray-500 truncate">
                          {prediction.structured_formatting?.secondary_text}
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {formData.address && (
            <div className="mt-2 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">Selected: {formData.address}</p>
            </div>
          )}
          
          <input
            type="hidden"
            name="address"
            value={formData.address}
            required
          />
        </div>

        <OpeningTimesSelector
          value={formData.opening_times}
          onChange={handleOpeningTimesChange}
        />

        <div>
          <label htmlFor="price" className="flex items-center gap-2 text-sm font-medium text-[#374151] mb-2">
            <PoundSterling size={16} className="text-[#374151]" />
            Price per Minute
          </label>
          <select
            id="price"
            name="price"
            value={formData.price}
            onChange={handleChange}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#374151] focus:border-transparent"
          >
            <option value="">Select price per minute</option>
            {priceOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div className="text-red-500 text-sm text-center p-3 bg-red-50 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="text-green-600 text-sm text-center p-3 bg-green-50 rounded-lg">
            Pod added successfully!
          </div>
        )}

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-[#374151] text-white py-3 rounded-lg hover:bg-[#374151]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {isSubmitting ? 'Adding Pod...' : 'Add Pod'}
        </button>
      </form>
    </div>
  );
};

export default PodAddForm;
